import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Calendar, Clock, TrendingUp, Plus, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/hr';

export default function HRDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_employees: 0,
    pending_leaves: 0,
    present_today: 0,
    on_leave_today: 0
  });
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, leavesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/leaves?status=pending`)
      ]);
      
      setStats(statsRes.data);
      setRecentLeaves(leavesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
    <div 
      onClick={onClick}
      className={`bg-white rounded-xl shadow-sm p-6 cursor-pointer hover:shadow-md transition-all ${onClick ? 'hover:scale-105' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{label}</p>
          <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
        </div>
        <div className={`${color} bg-opacity-10 p-3 rounded-lg`}>
          <Icon className={`w-8 h-8 ${color}`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading HR Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">HR Operations Dashboard</h1>
              <p className="text-gray-600 mt-1">Manager Console - Welcome back!</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/hr-ops/employees')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add Employee</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Users}
            label="Total Employees"
            value={stats.total_employees}
            color="text-blue-600"
            onClick={() => navigate('/hr-ops/employees')}
          />
          <StatCard
            icon={Calendar}
            label="Pending Leave Requests"
            value={stats.pending_leaves}
            color="text-orange-600"
            onClick={() => navigate('/hr-ops/leaves')}
          />
          <StatCard
            icon={Clock}
            label="Present Today"
            value={stats.present_today}
            color="text-green-600"
            onClick={() => navigate('/hr-ops/attendance')}
          />
          <StatCard
            icon={TrendingUp}
            label="On Leave Today"
            value={stats.on_leave_today}
            color="text-purple-600"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/hr-ops/employees')}
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <Users className="w-6 h-6 text-blue-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Employees</p>
                  <p className="text-sm text-gray-600">Manage team</p>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/hr-ops/leaves')}
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all"
              >
                <Calendar className="w-6 h-6 text-orange-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Leave Requests</p>
                  <p className="text-sm text-gray-600">Review & approve</p>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/hr-ops/attendance')}
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
              >
                <Clock className="w-6 h-6 text-green-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Attendance</p>
                  <p className="text-sm text-gray-600">Track & manage</p>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/hr-ops/employees/new')}
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
              >
                <Plus className="w-6 h-6 text-purple-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Add Employee</p>
                  <p className="text-sm text-gray-600">Onboard new hire</p>
                </div>
              </button>
            </div>
          </div>

          {/* Pending Leave Requests */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Pending Leave Requests</h3>
              <button
                onClick={() => navigate('/hr-ops/leaves')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
              </button>
            </div>
            
            {recentLeaves.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No pending leave requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLeaves.map(leave => (
                  <div
                    key={leave.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate('/hr-ops/leaves')}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{leave.employee_name}</p>
                      <p className="text-sm text-gray-600">
                        {leave.leave_type} - {leave.days} days
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {leave.start_date} to {leave.end_date}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      Pending
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="bg-blue-600 text-white p-2 rounded-lg">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">Manager Dashboard</h4>
              <p className="text-blue-800 text-sm">
                Manage your team efficiently. Track attendance, approve leaves, and maintain employee records all in one place.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
